import PropTypes from 'prop-types';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { css } from '@emotion/react';

import { contentfulApi } from 'lib/api/contentfulApi';

import { mobileOnly, tabletUp } from 'styles/utils';

import RichText from '../RichText/RichText';
import PromoList from '../PromoList/PromoList';
import PromoCards from '../PromoCards/PromoCards';
import {
  headerHeightMobile,
  headerHeightDesktop,
} from '../Navbar/Navbar.config';

const promosCss = css`
  padding-right: 0;
  padding-left: 0;

  ${mobileOnly} {
    margin: 32px 0;
  }

  ${tabletUp} {
    margin: 57px 0;
  }
`;

// Adding margin in order to compensate for the fixed navbar
const anchorTargetCss = css`
  position: absolute;

  ${mobileOnly} {
    margin-top: -${headerHeightMobile}px;
  }

  ${tabletUp} {
    margin-top: -${headerHeightDesktop}px;
  }
`;

/**
 * Render a post's rich text content.
 *
 * It makes use of Contentful's rich text different node types.
 *
 * @see
 * [Contentful: Rich Text](https://www.contentful.com/developers/docs/concepts/rich-text/)
 *
 * @param {Object} props
 */
function PostRichText({ text }) {
  const textOptions = {
    renderNode: {
      [BLOCKS.EMBEDDED_ENTRY](node) {
        const nodeTarget = node?.data?.target;
        const nodeContentType = nodeTarget?.sys?.contentType?.sys?.id;

        switch (nodeContentType) {
          // Render promos. Could be either rendered as a list or as cards
          case contentfulApi.contentTypes.INFO_CENTER_ARTICLE_PROMOS:
            return nodeTarget.renderAsList ? (
              <PromoList
                promos={contentfulApi.shapePromos(nodeTarget)}
                css={promosCss}
              />
            ) : (
              <PromoCards
                promos={contentfulApi.shapePromos(node.data.target)}
                inRichText
                css={promosCss}
              />
            );

          default:
            return null;
        }
      },
      [INLINES.HYPERLINK](node, children) {
        const linkHref = node.data.uri;
        const isAnchorTarget = linkHref[0] === '-';

        /*
        If the hyperlink starts with a secret hyphen (-), then
        it should be rendered as a target element for a jump marker anchor

        That's a hackish way of having jump markers inside a rich text
        content. Since Contentful doesn't provide that feature.
        */
        if (isAnchorTarget) {
          const nodeId = `${linkHref.slice(1)}`;

          return (
            <>
              <a id={nodeId} css={anchorTargetCss} />
              {children}
            </>
          );
        }

        return <a href={linkHref}>{children}</a>;
      },
    },
  };

  return <RichText text={text} textOptions={textOptions} />;
}

PostRichText.propTypes = {
  /** Post's rich text content to render */
  text: PropTypes.object.isRequired,
};

export default PostRichText;
